import { ImagePreview } from '@common/components/ImagePreview';
import Markdown from '@common/components/Markdown';
import { PostageOptions } from '@common/components/PostageOptions';
import SectionContainer from '@common/components/SectionContainer';
import Suggestions from '@common/components/Suggestions';
import { Heading4 } from '@common/components/Typography';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { media } from 'styled-bootstrap-grid';
import styled, { css } from 'styled-components';

import { addItemToBasket } from '@utils/basket/addToBasket';
import { getMainCategoryId, mergeCrossSellWithUpsell } from '@utils/helpers';

import { createBasket, updateBasket } from '@store/basket/actions';

import { basketSelectors } from '@store/basket/selectors';
import { entitySelectors } from '@store/entity/selectors';

import { BasketDrawer } from '@common/components/BasketDrawer/BasketDrawer';
import { calculateTotalNumberOfItemsInBasket } from '@common/components/BasketDrawer/BasketDrawer.helpers';
import { ModuleType } from '@common/components/BasketDrawer/BasketListing.types';
import { useModulesToRecommend } from '@common/components/BasketDrawer/hooks/useModulesToRecommend';
import { DiscountBanner } from '@common/components/Price';
import { ItemDetailsDescription } from '@features/AddedToBasket/Retail/ItemDetailsDescription';
import { spaces } from '@common/theme/spaces';
import { useTranslations } from '@lang';
import { isMobile } from '@utils/grid';
import { useGetSpaSettings } from '@features/spa';

const ProductWrapper = styled(SectionContainer)`
  padding-top: 20px;
  padding-bottom: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;

  ${media.md`
    flex-direction: row;
    align-items: flex-start;
    padding-top: 80px;
    padding-bottom: 80px;
  `}
`;

const ProductWrapperColumn = styled.div<{ sticky?: boolean }>`
  width: 100%;

  ${media.md`
    width: 50%;
  `}

  ${({ sticky }) =>
    sticky &&
    css`
      margin-right: -20px;
      margin-left: -20px;
      width: calc(100% + 40px);

      ${media.md`
        margin-right: 0;
        margin-left: 0;
        position: sticky;
        top: 20px;
      `}
    `}
`;

const DescriptionWrapper = styled(SectionContainer)`
  max-width: 760px;
  padding: 20px 0;
  line-height: 150%;

  ${media.md`
    padding: 40px 0 80px 60px;
  `}
`;

const Title = styled(Heading4)`
  margin-top: 0;
  margin-bottom: 25px;

  ${media.md`
    padding-left: 60px;
  `}
`;

/**
 * @deprecated Replaced with FulfilableProduct component
 * @todo Ensure FulfilableProduct captures the functionality of this component
 */
class Product extends Component<any, any> {
  constructor(props) {
    super(props);
    this.handleBasketUpdate = this.handleBasketUpdate.bind(this);
    this.hideSuccess = this.hideSuccess.bind(this);
    this.showSuccess = this.showSuccess.bind(this);
    this.getAddedProduct = this.getAddedProduct.bind(this);

    this.state = {
      isBasketSucceeded: false,
    };
  }

  getAddedProduct(): any {
    const { product, currentOption, currentQuantity } = this.props;

    return {
      product,
      option: currentOption,
      price: currentOption ? currentOption.price : product.price,
      discount: currentOption ? currentOption.discount : product.discount,
      quantity: currentQuantity,
    };
  }

  hideSuccess() {
    this.setState({
      isBasketSucceeded: false,
    });
  }

  showSuccess() {
    this.setState({
      isBasketSucceeded: true,
    });
  }

  handleBasketUpdate(data) {
    const { createBasket, updateBasket, property, basketDetails, language } = this.props;

    addItemToBasket({
      type: 'products',
      data,
      createBasket,
      updateBasket,
      property,
      basketDetails,
      language,
      onSuccess: () => {
        this.showSuccess();
      },
    });
  }

  render() {
    const { product, currentOption, property, basketDetails, t, modules, hideArrivalTimes } =
      this.props;
    const { isBasketSucceeded } = this.state;
    const mergedItems = mergeCrossSellWithUpsell(
      product.upSell,
      'product',
      product.crossSellObjects
    );

    return (
      <>
        {isBasketSucceeded && (
          <BasketDrawer
            currencyCode={basketDetails.total.currencyCode}
            numberOfItems={calculateTotalNumberOfItemsInBasket(basketDetails.items)}
            open={isBasketSucceeded}
            onClose={() => this.hideSuccess()}
            title={t('basketDrawer.title')}
            totalCost={basketDetails.total.amount}
            items={basketDetails?.items}
            modules={modules}
            hideArrivalTimes={hideArrivalTimes}
          />
        )}
        {product.purchaseLimit !== 0 && <div id="options-button" />}
        <ProductWrapper>
          {isMobile() && <Title>{product.name}</Title>}
          <ProductWrapperColumn sticky>
            <DiscountBanner discount={product.discount} margin={spaces.space5} />
            <ImagePreview images={product.imageGallery} altLabel={product.name} />
          </ProductWrapperColumn>
          <ProductWrapperColumn>
            {!isMobile() && <Title>{product.name}</Title>}
            <ItemDetailsDescription
              type="product"
              item={product}
              handleBasketUpdate={this.handleBasketUpdate}
              currentOption={currentOption}
              purchaseLimit={product.purchaseLimit}
            />
            <PostageOptions property={property} />
            <DescriptionWrapper>
              <Markdown text={product.description} />
            </DescriptionWrapper>
          </ProductWrapperColumn>
        </ProductWrapper>
        <Suggestions items={mergedItems} leadingCategoryId={getMainCategoryId(product)} />
      </>
    );
  }
}

Product['propTypes'] = {
  property: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
    address: PropTypes.string,
    deliveryEmail: PropTypes.bool,
    deliveryPost: PropTypes.bool,
    deliveryCollection: PropTypes.bool,
  }).isRequired,
  product: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
    upSell: PropTypes.object,
    crossSellObjects: PropTypes.array,
    imageGallery: PropTypes.array,
    crossSellTargetType: PropTypes.string,
    crossSellCategory: PropTypes.shape({
      id: PropTypes.number,
    }),
    description: PropTypes.string,
    options: PropTypes.array,
  }).isRequired,
  createBasket: PropTypes.func.isRequired,
  updateBasket: PropTypes.func.isRequired,
  basketDetails: PropTypes.shape({
    items: PropTypes.array,
  }),
  language: PropTypes.string.isRequired,
  currentOption: PropTypes.shape({
    price: PropTypes.object,
    name: PropTypes.string,
  }),
  currentQuantity: PropTypes.number,
};

Product['defaultProps'] = {
  basketDetails: {},
  currentOption: null,
  currentQuantity: null,
};

const mapStateToProps = (state, ownProps) => ({
  property: entitySelectors.getProperty(state),
  product: entitySelectors.getProduct(state, ownProps.id),
  basketDetails: basketSelectors.getBasketDetails(state),
  currentOption: basketSelectors.getBasketFormSelector(ownProps.id)(state, 'option'),
  currentQuantity: basketSelectors.getBasketFormSelector(ownProps.id)(state, 'quantity'),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      createBasket,
      updateBasket,
    },
    dispatch
  );

const ProductFeatureFlagWrapper = (props) => {
  const { t } = useTranslations();

  const modules = Object.keys(props.property.modulesExcludingDisabled);

  const modulesToRecommend = useModulesToRecommend({
    modules: modules as ModuleType[],
    items: props.basketDetails?.items ?? [],
  });
  const { data: spaSettings } = useGetSpaSettings(props.property.id);
  const hideArrivalTimes = spaSettings?.hideArrivalTimes || false;

  return (
    <Product t={t} modules={modulesToRecommend} hideArrivalTimes={hideArrivalTimes} {...props} />
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductFeatureFlagWrapper) as any;
